// Hooks do encaminhamento: padrao global, canais (origem/destino) das contas
// conectadas, rotas do modo avancado, overrides por canal e dados de saude.
// Leitura via RLS (.from), escrita sensivel via RPC SECURITY DEFINER.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  normalizeOverride,
  normalizeSettings,
  type ChannelOverride,
  type ForwardingPlatform,
  type ForwardingSettings,
  type RouteRow,
  type ValueFilter,
} from "@lucralink/shared";
import { supabase } from "@/lib/supabase";
import { serializeSettings, serializeValueFilter } from "@/lib/forwarding";

export type ChannelRole = "origem" | "destino";

export interface ForwardingChannel {
  readonly id: string;
  readonly platform: ForwardingPlatform;
  /** channel_id (Telegram) ou jid (WhatsApp): a chave usada pelo bot. */
  readonly ref: string;
  readonly title: string | null;
  readonly role: ChannelRole;
  readonly status: "active" | "paused" | "error_not_member";
}

const keys = {
  settings: ["forwarding-settings"] as const,
  channels: ["forwarding-channels"] as const,
  routes: ["forwarding-routes"] as const,
  overrides: ["forwarding-overrides"] as const,
  health: ["forwarding-health"] as const,
};

const CHANNEL_TABLES: Readonly<Record<ForwardingPlatform, { table: string; ref: string }>> = {
  telegram: { table: "mtproto_monitored_channels", ref: "channel_id" },
  whatsapp: { table: "whatsapp_monitored_chats", ref: "jid" },
};

// ---------------------------------------------------------------------------
// Padrao global
// ---------------------------------------------------------------------------

async function fetchSettings(): Promise<ForwardingSettings> {
  const { data, error } = await supabase
    .from("forwarding_settings")
    .select("*")
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return normalizeSettings(data);
}

export function useForwardingSettings() {
  return useQuery<ForwardingSettings, Error>({
    queryKey: keys.settings,
    queryFn: fetchSettings,
    staleTime: 30_000,
  });
}

export function useSaveForwardingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (settings: ForwardingSettings) => {
      const { error } = await supabase.rpc("save_forwarding_settings", {
        p_settings: serializeSettings(settings),
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.settings });
    },
  });
}

// ---------------------------------------------------------------------------
// Canais (origens e destinos) das contas conectadas
// ---------------------------------------------------------------------------

type RawChannel = Record<string, unknown>;

function toChannel(platform: ForwardingPlatform, raw: RawChannel): ForwardingChannel {
  const status = raw["status"];
  return {
    id: String(raw["id"]),
    platform,
    ref: String(raw[CHANNEL_TABLES[platform].ref] ?? ""),
    title: typeof raw["title"] === "string" ? raw["title"] : null,
    role: raw["role"] === "destino" ? "destino" : "origem",
    status: status === "paused" || status === "error_not_member" ? status : "active",
  };
}

async function fetchPlatformChannels(platform: ForwardingPlatform): Promise<ForwardingChannel[]> {
  const { table, ref } = CHANNEL_TABLES[platform];
  const { data, error } = await supabase
    .from(table)
    .select(`id, ${ref}, title, role, status, created_at`)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return ((data ?? []) as unknown as RawChannel[]).map((row) => toChannel(platform, row));
}

async function fetchChannels(): Promise<ForwardingChannel[]> {
  const [telegram, whatsapp] = await Promise.all([
    fetchPlatformChannels("telegram"),
    fetchPlatformChannels("whatsapp"),
  ]);
  return [...telegram, ...whatsapp];
}

export function useForwardingChannels() {
  return useQuery<ForwardingChannel[], Error>({
    queryKey: keys.channels,
    queryFn: fetchChannels,
    staleTime: 30_000,
  });
}

export interface ChannelUpdate {
  readonly channel: ForwardingChannel;
  readonly role?: ChannelRole;
  readonly status?: "active" | "paused";
}

/** Troca papel/status. O trigger do banco aplica o limite de destinos do plano. */
export function useUpdateForwardingChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ channel, role, status }: ChannelUpdate) => {
      // Papel so via RPC: destino Telegram exige prova de posse (bot admin).
      if (role) {
        const { error } = await supabase.rpc("set_forwarding_channel_role", {
          p_platform: channel.platform,
          p_channel_id: channel.id,
          p_role: role,
        });
        if (error) throw error;
      }
      if (status) {
        const { error } = await supabase
          .from(CHANNEL_TABLES[channel.platform].table)
          .update({ status })
          .eq("id", channel.id);
        if (error) throw error;
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.channels });
      await qc.invalidateQueries({ queryKey: keys.health });
    },
  });
}

// ---------------------------------------------------------------------------
// Rotas (modo avancado)
// ---------------------------------------------------------------------------

export interface PlatformRoute extends RouteRow {
  readonly platform: ForwardingPlatform;
}

async function fetchRoutes(): Promise<PlatformRoute[]> {
  const { data, error } = await supabase
    .from("forwarding_routes")
    .select("platform, source_ref, destination_ref");
  if (error) throw error;
  return ((data ?? []) as RawChannel[]).map((r) => ({
    platform: r["platform"] === "whatsapp" ? "whatsapp" : "telegram",
    sourceRef: String(r["source_ref"]),
    destinationRef: String(r["destination_ref"]),
  }));
}

export function useForwardingRoutes() {
  return useQuery<PlatformRoute[], Error>({
    queryKey: keys.routes,
    queryFn: fetchRoutes,
    staleTime: 30_000,
  });
}

export function useSetForwardingRoutes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      platform: ForwardingPlatform;
      sourceRef: string;
      destinationRefs: string[];
    }) => {
      const { error } = await supabase.rpc("set_forwarding_routes", {
        p_platform: input.platform,
        p_source_ref: input.sourceRef,
        p_destination_refs: input.destinationRefs,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.routes });
    },
  });
}

// ---------------------------------------------------------------------------
// Overrides por canal de origem
// ---------------------------------------------------------------------------

async function fetchOverrides(): Promise<ChannelOverride[]> {
  const { data, error } = await supabase.from("forwarding_channel_overrides").select("*");
  if (error) throw error;
  return ((data ?? []) as RawChannel[])
    .map(normalizeOverride)
    .filter((o): o is ChannelOverride => o !== null);
}

export function useChannelOverrides() {
  return useQuery<ChannelOverride[], Error>({
    queryKey: keys.overrides,
    queryFn: fetchOverrides,
    staleTime: 30_000,
  });
}

export interface OverrideInput {
  readonly platform: ForwardingPlatform;
  readonly channelRef: string;
  readonly allowedProviders: string[] | null;
  readonly ignoredTerms: string[];
  readonly linkStyle: "short" | "direct" | null;
  readonly valueFilter: ValueFilter | null;
}

export function useSaveChannelOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: OverrideInput) => {
      const { error } = await supabase.rpc("upsert_forwarding_channel_override", {
        p_platform: input.platform,
        p_channel_ref: input.channelRef,
        p_allowed_providers: input.allowedProviders,
        p_ignored_terms: input.ignoredTerms,
        p_link_style: input.linkStyle,
        p_value_filter: input.valueFilter ? serializeValueFilter(input.valueFilter) : null,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.overrides });
    },
  });
}

export function useDeleteChannelOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { platform: ForwardingPlatform; channelRef: string }) => {
      const { error } = await supabase.rpc("delete_forwarding_channel_override", {
        p_platform: input.platform,
        p_channel_ref: input.channelRef,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.overrides });
    },
  });
}

// ---------------------------------------------------------------------------
// Saude: credenciais configuradas, lojas vistas sem credencial, fila
// ---------------------------------------------------------------------------

export interface ForwardingHealthData {
  readonly credentials: ReadonlyArray<{ marketplace: string; status: string }>;
  /** Lojas que apareceram nos ultimos 7 dias sem credencial cadastrada. */
  readonly missingCredentialSeen: ReadonlyArray<{ marketplace: string; count: number }>;
  readonly pendingQueue: number;
  readonly failedQueue: number;
  /** "plataforma:ref" das origens que tem destino legado configurado. */
  readonly legacySourceKeys: readonly string[];
}

const HEALTH_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function countByMarketplace(rows: RawChannel[]): Array<{ marketplace: string; count: number }> {
  const counts = new Map<string, number>();
  for (const row of rows) {
    const code = typeof row["marketplace_code"] === "string" ? row["marketplace_code"] : null;
    if (code) counts.set(code, (counts.get(code) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([marketplace, count]) => ({ marketplace, count }))
    .sort((a, b) => b.count - a.count);
}

async function fetchHealth(): Promise<ForwardingHealthData> {
  const since = new Date(Date.now() - HEALTH_WINDOW_MS).toISOString();
  const [creds, attempts, queue, tgLegacy, waLegacy] = await Promise.all([
    supabase.from("affiliate_credentials_public").select("marketplace_code, status"),
    supabase
      .from("conversion_attempts")
      .select("marketplace_code")
      .eq("status", "error_no_credential")
      .gte("created_at", since)
      .limit(500),
    supabase
      .from("forwarding_queue")
      .select("status")
      .in("status", ["pending", "sending", "failed"])
      .limit(1000),
    supabase.from("mtproto_repost_configs").select("channel_id, destination"),
    supabase.from("whatsapp_repost_configs").select("jid, destination"),
  ]);
  const firstError =
    creds.error ?? attempts.error ?? queue.error ?? tgLegacy.error ?? waLegacy.error;
  if (firstError) throw firstError;
  const queueRows = (queue.data ?? []) as RawChannel[];
  return {
    credentials: ((creds.data ?? []) as RawChannel[]).map((r) => ({
      marketplace: String(r["marketplace_code"]),
      status: String(r["status"]),
    })),
    missingCredentialSeen: countByMarketplace((attempts.data ?? []) as RawChannel[]),
    pendingQueue: queueRows.filter((r) => r["status"] !== "failed").length,
    failedQueue: queueRows.filter((r) => r["status"] === "failed").length,
    legacySourceKeys: [
      ...legacyKeys("telegram", (tgLegacy.data ?? []) as RawChannel[], "channel_id"),
      ...legacyKeys("whatsapp", (waLegacy.data ?? []) as RawChannel[], "jid"),
    ],
  };
}

/** Origens com destino legado (repost_config.destination): o bot usa como fallback. */
function legacyKeys(platform: ForwardingPlatform, rows: RawChannel[], refColumn: string): string[] {
  return rows
    .filter((r) => typeof r["destination"] === "string" && r["destination"] !== "")
    .map((r) => `${platform}:${String(r[refColumn])}`);
}

export function useForwardingHealth() {
  return useQuery<ForwardingHealthData, Error>({
    queryKey: keys.health,
    queryFn: fetchHealth,
    staleTime: 60_000,
  });
}
